import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Save,
  Cpu,
  Info,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function ParamHelp({ meta }) {
  if (!meta) return null;
  return (
    <div className="mt-1 text-xs text-gray-500 space-y-1">
      <p>{meta.description}</p>
      <p>
        <span className="font-medium text-gray-600">Effect: </span>
        {meta.effect}
      </p>
    </div>
  );
}

function GeminiConfiguration() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [catalog, setCatalog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingAssistantId, setSavingAssistantId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showParamHelp, setShowParamHelp] = useState(true);

  const [globalForm, setGlobalForm] = useState({
    model: "",
    temperature: "",
    topK: "",
    topP: "",
  });

  const [assistantForms, setAssistantForms] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [catalogRes, settingsRes] = await Promise.all([
        axios.get("/api/gemini-admin/catalog"),
        axios.get("/api/gemini-admin/settings"),
      ]);
      setCatalog(catalogRes.data);
      setSettings(settingsRes.data);

      const stored = settingsRes.data.global?.stored || {};
      const effective = settingsRes.data.global?.effective || {};
      setGlobalForm({
        model: stored.model ?? effective.model ?? catalogRes.data.defaults.model,
        temperature:
          stored.temperature ??
          effective.temperature ??
          catalogRes.data.defaults.temperature,
        topK: stored.topK ?? effective.topK ?? catalogRes.data.defaults.topK,
        topP: stored.topP ?? effective.topP ?? catalogRes.data.defaults.topP,
      });

      const forms = {};
      for (const a of settingsRes.data.assistants || []) {
        forms[a._id] = {
          useGlobalDefaults: Object.values(a.inherits || {}).every(Boolean),
          model: a.overrides?.model ?? "",
          temperature: a.overrides?.temperature ?? "",
          topK: a.overrides?.topK ?? "",
          topP: a.overrides?.topP ?? "",
        };
      }
      setAssistantForms(forms);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load Gemini configuration",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGlobalChange = (field, value) => {
    setGlobalForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAssistantChange = (id, field, value) => {
    setAssistantForms((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const saveGlobal = async () => {
    setSavingGlobal(true);
    setError("");
    setSuccess("");
    try {
      await axios.put("/api/gemini-admin/settings/global", {
        model: globalForm.model,
        temperature: Number(globalForm.temperature),
        topK: Number(globalForm.topK),
        topP: Number(globalForm.topP),
      });
      setSuccess("Global Gemini settings saved. New sessions will use these values.");
      await loadData();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(
        errors?.join("; ") ||
          err.response?.data?.message ||
          "Failed to save global settings",
      );
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveAssistant = async (assistantId) => {
    const form = assistantForms[assistantId];
    if (!form) return;

    setSavingAssistantId(assistantId);
    setError("");
    setSuccess("");
    try {
      const payload = form.useGlobalDefaults
        ? { useGlobalDefaults: true }
        : {
            ...(form.model ? { model: form.model } : { model: null }),
            ...(form.temperature !== ""
              ? { temperature: Number(form.temperature) }
              : { temperature: null }),
            ...(form.topK !== "" ? { topK: Number(form.topK) } : { topK: null }),
            ...(form.topP !== "" ? { topP: Number(form.topP) } : { topP: null }),
          };

      await axios.put(`/api/gemini-admin/assistants/${assistantId}`, payload);
      setSuccess("Assistant override saved.");
      await loadData();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(
        errors?.join("; ") ||
          err.response?.data?.message ||
          "Failed to save assistant settings",
      );
    } finally {
      setSavingAssistantId(null);
    }
  };

  const params = catalog?.parameters || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Gemini Configuration
              </h1>
            </div>
            <span className="text-sm text-gray-600">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0 space-y-6">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
              {success}
            </div>
          )}

          <div className="card p-4 flex items-start gap-3 bg-blue-50 border-blue-100">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium">How settings apply</p>
              <p className="mt-1 text-blue-800">
                Priority: per-assistant override → global admin setting →
                server .env → built-in defaults. Changes affect{" "}
                <strong>new chat sessions only</strong> — existing sessions keep
                their snapshot.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="card p-10 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
              <p className="mt-3 text-gray-500">Loading configuration...</p>
            </div>
          ) : (
            <>
              {/* Global defaults */}
              <section className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-5 w-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      Global defaults
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowParamHelp((v) => !v)}
                    className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
                  >
                    {showParamHelp ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Parameter guide
                  </button>
                </div>

                {settings?.global?.updatedAt && (
                  <p className="text-xs text-gray-500 mb-4">
                    Last updated {new Date(settings.global.updatedAt).toLocaleString()}
                    {settings.global.updatedBy
                      ? ` by ${settings.global.updatedBy}`
                      : ""}
                  </p>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {params.model?.label || "Model"}
                    </label>
                    <select
                      className="input-field"
                      value={globalForm.model}
                      onChange={(e) => handleGlobalChange("model", e.target.value)}
                    >
                      {(catalog?.models || []).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label} — {m.capabilityLabel} · {m.costLabel}
                          {m.recommended ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                    {showParamHelp && <ParamHelp meta={params.model} />}
                    {globalForm.model && (
                      <p className="mt-2 text-xs text-gray-500">
                        {
                          catalog?.models?.find((m) => m.id === globalForm.model)
                            ?.description
                        }
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {params.temperature?.label} ({globalForm.temperature})
                    </label>
                    <input
                      type="range"
                      min={params.temperature?.min ?? 0}
                      max={params.temperature?.max ?? 2}
                      step={params.temperature?.step ?? 0.05}
                      value={globalForm.temperature}
                      onChange={(e) =>
                        handleGlobalChange("temperature", e.target.value)
                      }
                      className="w-full"
                    />
                    <input
                      type="number"
                      className="input-field mt-2"
                      min={params.temperature?.min}
                      max={params.temperature?.max}
                      step={params.temperature?.step}
                      value={globalForm.temperature}
                      onChange={(e) =>
                        handleGlobalChange("temperature", e.target.value)
                      }
                    />
                    {showParamHelp && <ParamHelp meta={params.temperature} />}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {params.topK?.label} ({globalForm.topK})
                    </label>
                    <input
                      type="range"
                      min={params.topK?.min ?? 1}
                      max={params.topK?.max ?? 40}
                      step={1}
                      value={globalForm.topK}
                      onChange={(e) => handleGlobalChange("topK", e.target.value)}
                      className="w-full"
                    />
                    <input
                      type="number"
                      className="input-field mt-2"
                      min={params.topK?.min}
                      max={params.topK?.max}
                      step={1}
                      value={globalForm.topK}
                      onChange={(e) => handleGlobalChange("topK", e.target.value)}
                    />
                    {showParamHelp && <ParamHelp meta={params.topK} />}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {params.topP?.label} ({globalForm.topP})
                    </label>
                    <input
                      type="range"
                      min={params.topP?.min ?? 0}
                      max={params.topP?.max ?? 1}
                      step={params.topP?.step ?? 0.05}
                      value={globalForm.topP}
                      onChange={(e) => handleGlobalChange("topP", e.target.value)}
                      className="w-full"
                    />
                    <input
                      type="number"
                      className="input-field mt-2"
                      min={params.topP?.min}
                      max={params.topP?.max}
                      step={params.topP?.step}
                      value={globalForm.topP}
                      onChange={(e) => handleGlobalChange("topP", e.target.value)}
                    />
                    {showParamHelp && <ParamHelp meta={params.topP} />}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={saveGlobal}
                    disabled={savingGlobal}
                    className="btn-primary flex items-center disabled:opacity-60"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingGlobal ? "Saving..." : "Save global defaults"}
                  </button>
                  <button
                    type="button"
                    onClick={loadData}
                    className="btn-secondary flex items-center"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </section>

              {/* Per-assistant overrides */}
              <section className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Per-assistant overrides
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Gemini assistants only. Leave &quot;Use global defaults&quot; checked
                  to inherit the global model and sampling parameters.
                </p>

                {!settings?.assistants?.length ? (
                  <p className="text-sm text-gray-500">No Gemini assistants found.</p>
                ) : (
                  <div className="space-y-6">
                    {settings.assistants.map((assistant) => {
                      const form = assistantForms[assistant._id] || {};
                      return (
                        <div
                          key={assistant._id}
                          className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {assistant.name}
                              </p>
                              <p className="text-xs text-gray-500 font-mono">
                                {assistant.key}
                              </p>
                            </div>
                            <div className="text-right text-xs text-gray-600">
                              <p>
                                Effective:{" "}
                                <span className="font-mono">
                                  {assistant.effective?.model}
                                </span>
                              </p>
                              <p>
                                temp {assistant.effective?.temperature} · topK{" "}
                                {assistant.effective?.topK} · topP{" "}
                                {assistant.effective?.topP}
                              </p>
                            </div>
                          </div>

                          <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                            <input
                              type="checkbox"
                              checked={!!form.useGlobalDefaults}
                              onChange={(e) =>
                                handleAssistantChange(
                                  assistant._id,
                                  "useGlobalDefaults",
                                  e.target.checked,
                                )
                              }
                              className="rounded border-gray-300"
                            />
                            Use global defaults
                          </label>

                          {!form.useGlobalDefaults && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Model override
                                </label>
                                <select
                                  className="input-field text-sm"
                                  value={form.model || ""}
                                  onChange={(e) =>
                                    handleAssistantChange(
                                      assistant._id,
                                      "model",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">Inherit global</option>
                                  {(catalog?.models || []).map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Temperature
                                </label>
                                <input
                                  type="number"
                                  className="input-field text-sm"
                                  placeholder="Inherit"
                                  min={params.temperature?.min}
                                  max={params.temperature?.max}
                                  step={params.temperature?.step}
                                  value={form.temperature}
                                  onChange={(e) =>
                                    handleAssistantChange(
                                      assistant._id,
                                      "temperature",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Top K
                                </label>
                                <input
                                  type="number"
                                  className="input-field text-sm"
                                  placeholder="Inherit"
                                  min={params.topK?.min}
                                  max={params.topK?.max}
                                  value={form.topK}
                                  onChange={(e) =>
                                    handleAssistantChange(
                                      assistant._id,
                                      "topK",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Top P
                                </label>
                                <input
                                  type="number"
                                  className="input-field text-sm"
                                  placeholder="Inherit"
                                  min={params.topP?.min}
                                  max={params.topP?.max}
                                  step={params.topP?.step}
                                  value={form.topP}
                                  onChange={(e) =>
                                    handleAssistantChange(
                                      assistant._id,
                                      "topP",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={() => saveAssistant(assistant._id)}
                            disabled={savingAssistantId === assistant._id}
                            className="btn-primary text-sm py-1.5 px-3 disabled:opacity-60"
                          >
                            {savingAssistantId === assistant._id
                              ? "Saving..."
                              : "Save assistant"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Model reference table */}
              <section className="card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Model reference (highest → lowest capability / cost)
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                          Model
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                          Capability
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                          Relative cost
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(catalog?.models || []).map((m) => (
                        <tr key={m.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{m.label}</p>
                            <p className="text-xs font-mono text-gray-500">{m.id}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {m.capabilityLabel}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{m.costLabel}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                m.stability === "stable"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-amber-100 text-amber-800"
                              }`}
                            >
                              {m.stability}
                              {m.recommended ? " · default" : ""}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default GeminiConfiguration;
